import React, { useMemo, useState } from "react";
import { Autocomplete, Button, Dialog, IconButton, TextField } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import { TypeIcon } from "./WorkHierarchyTable";
import { levelOf, validParents } from "./treeUtils";
import "../Modal/modal.scss";

/** Pick the epic or story an issue belongs under. */
const ParentPickerDialog = ({ open, issue, roots, onClose, onSave }) => {
  // grouped as Epics then Stories, so they have to arrive in that order
  const options = useMemo(
    () => (issue ? validParents(roots, issue).sort((a, b) => levelOf(a.issueType) - levelOf(b.issueType)) : []),
    [roots, issue]
  );
  const current = options.find((o) => String(o._id) === String(issue?.parent)) || null;
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  const allowedText = levelOf(issue?.issueType) === 1
    ? "A story can sit under an epic."
    : "A task or bug can sit under an epic or a story.";

  const save = async () => {
    setSaving(true);
    try {
      await onSave(issue, value?._id || null);
      onClose();
    } catch {
      // the page has already said why
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="wh-parent-title"
      PaperProps={{ className: "pj-modal pj-modal--form" }}
      slotProps={{ backdrop: { className: "pj-modal-backdrop" } }}
    >
      <div className="pj-modal-body">
        <IconButton className="pj-modal-close" size="small" onClick={onClose} aria-label="Close" disabled={saving}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
        <div className="pj-modal-head">
          <span className="pj-modal-badge"><AccountTreeRoundedIcon /></span>
          <div>
            <h3 className="pj-modal-title" id="wh-parent-title">Move {issue?.name}</h3>
            <p className="pj-modal-sub">{allowedText}</p>
          </div>
        </div>

        <div className="pj-field-group">
          <label className="pj-field-label" htmlFor="wh-parent-input">Parent</label>
          {options.length ? (
            <Autocomplete
              id="wh-parent-input"
              options={options}
              value={value}
              onChange={(_e, next) => setValue(next)}
              getOptionLabel={(o) => `${o.name} ${o.summary || ""}`}
              isOptionEqualToValue={(a, b) => String(a._id) === String(b._id)}
              groupBy={(o) => (levelOf(o.issueType) === 0 ? "Epics" : "Stories")}
              renderOption={(props, o) => {
                const { key, ...rest } = props;
                return (
                  <li key={o._id} {...rest} className={`${rest.className} wh-parent-option`}>
                    <TypeIcon type={o.issueType} />
                    <strong>{o.name}</strong>
                    <span>{o.summary}</span>
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} size="small" placeholder="Search epics and stories" autoFocus />}
            />
          ) : (
            <p className="pj-modal-sub">
              {levelOf(issue?.issueType) === 1 ? "No epics exist" : "No epics or stories exist"} in this project yet.
            </p>
          )}
        </div>
      </div>
      <div className="pj-modal-actions">
        <Button className="pj-btn pj-btn--quiet" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button className="pj-btn pj-btn--primary" onClick={save} disabled={saving || (value?._id || null) === (current?._id || null)}>
          {saving ? "Saving…" : value ? `Move under ${value.name}` : "Remove parent"}
        </Button>
      </div>
    </Dialog>
  );
};

export default ParentPickerDialog;
